// Payment proofs — a QRIS screenshot or transfer receipt attached to a sale.
//
// The first browser-side upload in this app. Everything else that lands in
// storage (invoice PDFs) is written by an edge function as the service role.
// Here the cashier's own session uploads, which is why the bucket policies
// gate on is_staff() rather than being absent.
//
// Files live in a PRIVATE bucket and are read through short-lived signed URLs:
// a transfer receipt shows the payer's account and name.

import { supabase } from '@/lib/supabase';
import { tsReq } from './mappers';

const BUCKET = 'payment-proofs';
export const PROOF_MAX_BYTES = 5 * 1024 * 1024;
export const PROOF_MIME = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
export const PROOF_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
/** Long enough to view or print; short enough that a shared link goes stale. */
const SIGNED_URL_SECONDS = 60 * 60;

export interface PaymentProof {
  id: string;
  transactionId: string;
  paymentId: string | null;
  storagePath: string;
  mimeType: string;
  sizeBytes: number;
  note: string | null;
  uploadedByName: string | null;
  createdAt: number;
  /** Signed URL, valid for about an hour from the time of listing. */
  url: string | null;
}

export async function listPaymentProofs(transactionId: string): Promise<PaymentProof[]> {
  const { data, error } = await supabase
    .from('payment_proofs')
    .select('*')
    .eq('transaction_id', transactionId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  const rows = data ?? [];
  if (rows.length === 0) return [];

  const { data: signed, error: signErr } = await supabase.storage
    .from(BUCKET)
    .createSignedUrls(rows.map((r) => r.storage_path), SIGNED_URL_SECONDS);
  if (signErr) throw new Error(signErr.message);
  const urlByPath = new Map((signed ?? []).map((s) => [s.path, s.signedUrl]));

  return rows.map((r) => ({
    id: r.id,
    transactionId: r.transaction_id,
    paymentId: r.payment_id,
    storagePath: r.storage_path,
    mimeType: r.mime_type,
    sizeBytes: r.size_bytes,
    note: r.note,
    uploadedByName: r.uploaded_by_name,
    createdAt: tsReq(r.created_at),
    url: urlByPath.get(r.storage_path) ?? null,
  }));
}

export interface UploadProofInput {
  transactionId: string;
  file: File;
  /** Attach to a specific instalment (piutang). Omit for the sale itself. */
  paymentId?: string | null;
  note?: string;
}

function extensionFor(file: File): string {
  const fromName = file.name.split('.').pop()?.toLowerCase();
  if (fromName && /^[a-z0-9]{2,5}$/.test(fromName)) return fromName;
  return { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'application/pdf': 'pdf' }[file.type] ?? 'bin';
}

export async function uploadPaymentProof(input: UploadProofInput): Promise<void> {
  const { file } = input;
  // Checked here for the message; the bucket enforces the same limits.
  if (!PROOF_MIME.includes(file.type)) {
    throw new Error('Format harus JPG, PNG, WebP, atau PDF.');
  }
  if (file.size > PROOF_MAX_BYTES) {
    throw new Error(`File terlalu besar (${(file.size / 1024 / 1024).toFixed(1)} MB). Maksimal 5 MB.`);
  }

  const { data: auth } = await supabase.auth.getUser();
  const uid = auth.user?.id;
  if (!uid) throw new Error('Not signed in');
  const { data: profile } = await supabase
    .from('profiles').select('display_name').eq('id', uid).maybeSingle();

  // <transactionId>/<random>.<ext> — grouped per sale, never guessable.
  const path = `${input.transactionId}/${crypto.randomUUID()}.${extensionFor(file)}`;
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false });
  if (upErr) throw new Error(`Upload gagal: ${upErr.message}`);

  const { error } = await supabase.from('payment_proofs').insert({
    transaction_id: input.transactionId,
    payment_id: input.paymentId ?? null,
    storage_path: path,
    mime_type: file.type,
    size_bytes: file.size,
    note: input.note?.trim() || null,
    uploaded_by: uid,
    uploaded_by_name: profile?.display_name ?? null,
  });
  if (error) {
    // The file is in the bucket but the row failed: remove it, or it is an
    // orphan nobody can see or delete from the UI.
    await supabase.storage.from(BUCKET).remove([path]);
    throw new Error(error.message);
  }
}

/** Owner only (RLS). Removes the file first so a failed row delete cannot orphan it. */
export async function deletePaymentProof(proof: PaymentProof): Promise<void> {
  const { error: rmErr } = await supabase.storage.from(BUCKET).remove([proof.storagePath]);
  if (rmErr) throw new Error(rmErr.message);
  const { error } = await supabase.from('payment_proofs').delete().eq('id', proof.id);
  if (error) throw new Error(error.message);
}

/** How many proofs each of these transactions has — for a list indicator. */
export async function countPaymentProofs(transactionIds: string[]): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  if (transactionIds.length === 0) return counts;
  const { data, error } = await supabase
    .from('payment_proofs')
    .select('transaction_id')
    .in('transaction_id', transactionIds);
  if (error) throw new Error(error.message);
  for (const r of data ?? []) counts.set(r.transaction_id, (counts.get(r.transaction_id) ?? 0) + 1);
  return counts;
}
